/** Height of the chart area in px; consumers stack rows directly beneath it, so it never changes at runtime. */
export const AMOUNT_RANGE_BAR_HEIGHT_PX = 86;

/** Minimum clearance between the median label and an end label on the top row. */
const LABEL_GAP_PX = 8;

export interface IAmountRangeBarValues {
  min: number;
  max: number;
  median: number;
  current: number;
}

/** Rendered widths, read synchronously from the DOM before paint. */
export interface IAmountRangeBarMetrics {
  containerWidth: number;
  minLabelWidth: number;
  maxLabelWidth: number;
  /** Width of the "Median: " prefix alone; the value is measured separately so the prefix can be dropped. */
  medianPrefixWidth: number;
  medianValueWidth: number;
  currentLabelWidth: number;
}

export interface IAmountRangeBarLayout {
  /** Fraction along the track, 0..1, already clamped. */
  medianRatio: number;
  currentRatio: number;
  showMinLabel: boolean;
  showMaxLabel: boolean;
  showMedianPrefix: boolean;
  /** Pixels added to the label's centered position so it clears the container edges and its neighbors. */
  medianLabelShift: number;
  currentLabelShift: number;
}

/** Finite values with a non-inverted range are the only ones the bar can draw honestly. */
export function isRenderableRange({ min, max, median, current }: IAmountRangeBarValues): boolean {
  return [min, max, median, current].every(Number.isFinite) && max >= min;
}

/**
 * Fraction of the track a value sits at, clamped to the ends.
 *
 * A collapsed range (`min === max`) has no scale, so an in-range value sits at the center
 * and an outlier still snaps to the end it overshoots.
 */
export function positionRatio(value: number, min: number, max: number): number {
  const span = max - min;
  if (span <= 0) {
    return value < min ? 0 : value > max ? 1 : 0.5;
  }
  return Math.min(1, Math.max(0, (value - min) / span));
}

/**
 * Shift that keeps a label of `width` centered on `center` inside `[lo, hi]`, or null when it cannot fit.
 */
function fitShift(center: number, width: number, lo: number, hi: number): number | null {
  if (width > hi - lo) {
    return null;
  }
  const left = center - width / 2;
  if (left < lo) {
    return lo - left;
  }
  if (left + width > hi) {
    return hi - (left + width);
  }
  return 0;
}

/**
 * Resolves marker positions and label placement for one set of values.
 *
 * Without metrics (before the first measurement) every label is simply centered on its marker.
 * With metrics, the top row degrades in order so the bar's height never changes: shift the
 * median label clear of the end labels, then drop its "Median: " prefix, then hide the end
 * label nearest to it. The bottom row only ever holds the current label, so it just clamps to
 * the container edges — an out-of-range current amount ends up flush with the track end.
 */
export function resolveAmountRangeBarLayout(
  values: IAmountRangeBarValues,
  metrics?: IAmountRangeBarMetrics | null
): IAmountRangeBarLayout {
  const { min, max, median, current } = values;
  const medianRatio = positionRatio(median, min, max);
  const currentRatio = positionRatio(current, min, max);
  // An end label whose value the current label already states is redundant; a collapsed range needs only one end.
  const valueShowsMin = current !== min;
  const valueShowsMax = current !== max && max !== min;

  if (!metrics) {
    return {
      medianRatio,
      currentRatio,
      showMinLabel: valueShowsMin,
      showMaxLabel: valueShowsMax,
      showMedianPrefix: true,
      medianLabelShift: 0,
      currentLabelShift: 0,
    };
  }

  const { containerWidth, minLabelWidth, maxLabelWidth, medianPrefixWidth, medianValueWidth, currentLabelWidth } =
    metrics;
  const currentLabelShift = fitShift(currentRatio * containerWidth, currentLabelWidth, 0, containerWidth) ?? 0;

  const medianCenter = medianRatio * containerWidth;
  const hideNearerEnd =
    medianRatio < 0.5 ? { showMin: false, showMax: valueShowsMax } : { showMin: valueShowsMin, showMax: false };
  const attempts = [
    { showPrefix: true, showMin: valueShowsMin, showMax: valueShowsMax },
    { showPrefix: false, showMin: valueShowsMin, showMax: valueShowsMax },
    { showPrefix: true, ...hideNearerEnd },
    { showPrefix: false, ...hideNearerEnd },
  ];

  for (const attempt of attempts) {
    const width = medianValueWidth + (attempt.showPrefix ? medianPrefixWidth : 0);
    const lo = attempt.showMin ? minLabelWidth + LABEL_GAP_PX : 0;
    const hi = attempt.showMax ? containerWidth - maxLabelWidth - LABEL_GAP_PX : containerWidth;
    const shift = fitShift(medianCenter, width, lo, hi);
    if (shift !== null) {
      return {
        medianRatio,
        currentRatio,
        showMinLabel: attempt.showMin,
        showMaxLabel: attempt.showMax,
        showMedianPrefix: attempt.showPrefix,
        medianLabelShift: shift,
        currentLabelShift,
      };
    }
  }

  // Nothing fits on the row: keep the bare value inside the container and let the far end label stand.
  return {
    medianRatio,
    currentRatio,
    showMinLabel: hideNearerEnd.showMin,
    showMaxLabel: hideNearerEnd.showMax,
    showMedianPrefix: false,
    medianLabelShift: fitShift(medianCenter, medianValueWidth, 0, containerWidth) ?? 0,
    currentLabelShift,
  };
}
